import React, { useState } from "react";
import { Drawer, Form, Input, Select, DatePicker, Button, Space, message, Card, Row, Col, InputNumber } from "antd";
import { SaveOutlined, ReloadOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Option } = Select;

const ActionItemCreateDrawer = ({ visible, onClose, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [jsonErrors, setJsonErrors] = useState({});

    // 验证JSON格式
    const validateJson = (value, fieldName) => {
        if (!value || value.trim() === "") {
            setJsonErrors(prev => ({ ...prev, [fieldName]: null }));
            return null;
        }
        
        try {
            JSON.parse(value);
            setJsonErrors(prev => ({ ...prev, [fieldName]: null }));
            return null;
        } catch (error) {
            const errorMsg = `JSON格式错误: ${error.message}`;
            setJsonErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
            return errorMsg;
        }
    };

    // 处理创建
    const handleCreate = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            // 检查JSON字段是否有错误
            const hasJsonErrors = Object.values(jsonErrors).some(error => error !== null);
            if (hasJsonErrors) {
                message.error("请修复JSON格式错误后再保存");
                return;
            }

            // 转换JSON字段
            const processedValues = {
                ...values,
                due_date: values.due_date ? values.due_date.toISOString() : null,
                deadline: values.deadline ? values.deadline.toISOString() : null,
                suggested_payload: values.suggested_payload ? JSON.parse(values.suggested_payload) : null,
                user_modified_payload: values.user_modified_payload ? JSON.parse(values.user_modified_payload) : null,
                additional_info: values.additional_info ? JSON.parse(values.additional_info) : null,
            };

            // 注意：当前API没有创建接口，需要后端支持
            message.warning("创建功能暂未实现，需要后端API支持");
            console.log("创建数据:", processedValues);
            
            // 模拟成功
            // message.success("创建成功");
            // onSuccess();
            // onClose();
            // form.resetFields();
            // setJsonErrors({});
        } catch (error) {
            console.error("创建失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "创建失败";
            message.error(`创建失败: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    // 重置表单
    const handleReset = () => {
        form.resetFields();
        setJsonErrors({});
    };

    // 关闭时重置表单
    const handleClose = () => {
        form.resetFields();
        setJsonErrors({});
        onClose();
    };

    return (
        <Drawer
            title="创建新行动项"
            width={800}
            onClose={handleClose}
            open={visible}
            extra={
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                        重置
                    </Button>
                    <Button 
                        type="primary" 
                        icon={<SaveOutlined />} 
                        onClick={handleCreate}
                        loading={loading}
                    >
                        创建
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical" initialValues={{ status: "PENDING_AI", priority: 2 }}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item 
                            label="状态" 
                            name="status"
                            rules={[{ required: true, message: "请选择状态" }]}
                        >
                            <Select placeholder="选择状态">
                                <Option value="PENDING_AI">待AI处理</Option>
                                <Option value="PENDING_REVIEW">待人工审核</Option>
                                <Option value="PROCESSING">处理中</Option>
                                <Option value="COMPLETED">已完成</Option>
                                <Option value="FAILED">处理失败</Option>
                                <Option value="REJECTED">已拒绝</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="优先级" name="priority">
                            <Select placeholder="选择优先级">
                                <Option value={1}>高</Option>
                                <Option value={2}>中</Option>
                                <Option value={3}>低</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item 
                            label="来源类型" 
                            name="source_type"
                            rules={[{ required: true, message: "请选择来源类型" }]}
                        >
                            <Select placeholder="选择来源类型">
                                <Option value="FLIGHT_MESSAGE_MVT">航班消息MVT</Option>
                                <Option value="FLIGHT_MESSAGE_OTHER">航班消息其他</Option>
                                <Option value="SYSTEM_FLIGHT_CONFLICT_ALERT">系统航班冲突告警</Option>
                                <Option value="EMAIL_PROCESSING">邮件处理</Option>
                                <Option value="USER_REQUEST">用户请求</Option>
                                <Option value="SYSTEM_AUTO">系统自动</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="来源引用ID" name="source_ref_id">
                            <Input placeholder="输入来源引用ID" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Agent类型" name="agent_type">
                            <Select placeholder="选择Agent类型" allowClear>
                                <Option value="MVT_PROCESSING_AGENT_V2">MVT处理Agent</Option>
                                <Option value="CATERING_AGENT">航食Agent</Option>
                                <Option value="PERMIT_REQUEST_AGENT">许可申请Agent</Option>
                                <Option value="GENERAL_PROCESSING_AGENT">通用处理Agent</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="建议变更类型" name="suggested_change_type">
                            <Input placeholder="输入建议变更类型" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item label="关联航班ID" name="flight_operation_id">
                            <InputNumber style={{ width: "100%" }} placeholder="输入航班ID" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="关联邮件ID" name="related_email_id">
                            <InputNumber style={{ width: "100%" }} placeholder="输入邮件ID" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="事务ID" name="transaction_id">
                            <InputNumber style={{ width: "100%" }} placeholder="输入事务ID" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="事务类型" name="transaction_type">
                            <Input placeholder="输入事务类型，如CATERING_ORDER" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="关联对象类型" name="related_object_type">
                            <Input placeholder="输入关联对象类型，如AIRCRAFT" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item label="关联对象ID" name="related_object_id">
                    <Input placeholder="输入关联对象ID" />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="截止日期" name="due_date">
                            <DatePicker 
                                style={{ width: "100%" }}
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="处理截止时间" name="deadline">
                            <DatePicker 
                                style={{ width: "100%" }}
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item 
                    label="原始输入内容" 
                    name="raw_input_content"
                    rules={[{ required: true, message: "请输入原始输入内容" }]}
                >
                    <TextArea rows={4} placeholder="输入原始内容" />
                </Form.Item>

                <Form.Item label="AI处理备注" name="ai_processing_notes">
                    <TextArea rows={3} placeholder="输入AI处理备注" />
                </Form.Item>

                <Form.Item label="用户备注" name="user_remarks">
                    <TextArea rows={3} placeholder="输入用户备注" />
                </Form.Item>

                {/* JSON字段 */}
                <Card title="JSON字段" size="small">
                    <Form.Item 
                        label="建议载荷 (suggested_payload)" 
                        name="suggested_payload"
                        validateStatus={jsonErrors.suggested_payload ? "error" : ""}
                        help={jsonErrors.suggested_payload}
                    >
                        <TextArea 
                            rows={6} 
                            placeholder='输入JSON格式的建议载荷，例如：{"operation": "update", "data": {}}'
                            onChange={(e) => validateJson(e.target.value, "suggested_payload")}
                        />
                    </Form.Item>

                    <Form.Item 
                        label="用户修改载荷 (user_modified_payload)" 
                        name="user_modified_payload"
                        validateStatus={jsonErrors.user_modified_payload ? "error" : ""}
                        help={jsonErrors.user_modified_payload}
                    >
                        <TextArea 
                            rows={6} 
                            placeholder='输入JSON格式的用户修改载荷，例如：{"modified_data": {}}'
                            onChange={(e) => validateJson(e.target.value, "user_modified_payload")}
                        />
                    </Form.Item>

                    <Form.Item 
                        label="附加信息 (additional_info)" 
                        name="additional_info"
                        validateStatus={jsonErrors.additional_info ? "error" : ""}
                        help={jsonErrors.additional_info}
                    >
                        <TextArea 
                            rows={4} 
                            placeholder='输入JSON格式的附加信息，例如：{"notes": "additional notes"}'
                            onChange={(e) => validateJson(e.target.value, "additional_info")}
                        />
                    </Form.Item>
                </Card>
            </Form>
        </Drawer>
    );
};

export default ActionItemCreateDrawer;
